import React, { useState } from "react";

export const REFERRAL_LEVELS = ["level1", "level2", "level3", "level4", "level5"];

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

export const formatDateExport = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

export const formatNum = (n) =>
  n != null && !Number.isNaN(Number(n))
    ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 8 })
    : "—";

export const formatNumPlain = (n) =>
  n != null && !Number.isNaN(Number(n))
    ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 8, useGrouping: false })
    : "";

export const userFullName = (u) => {
  if (!u) return "—";
  const parts = [u.first_name ?? u.firstName, u.last_name ?? u.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

export const getSubscriptionId = (row) => row?.subscription_id ?? row?._id ?? row?.id ?? null;

export const stakeReportMatchesSearch = (row, query) => {
  if (!query || !String(query).trim()) return true;
  const q = String(query).trim().toLowerCase();
  const staker = row?.staker || {};
  const fields = [
    row?.subscription_id,
    staker.id,
    staker._id,
    staker.uuid,
    staker.email,
    staker.emailId,
    staker.phone,
    staker.mobileNumber,
    staker.first_name,
    staker.firstName,
    staker.last_name,
    staker.lastName,
    userFullName(staker),
    row?.staking?.currency,
    row?.package?.currency,
  ];

  return fields.some((value) => {
    if (value == null || value === "" || value === "—") return false;
    return String(value).toLowerCase().includes(q);
  });
};

export const StatusBadge = ({ status }) => {
  const st = String(status ?? "").toUpperCase();
  const cls =
    st === "ACTIVE"
      ? "stake-report-status--active"
      : st === "COMPLETED"
        ? "stake-report-status--completed"
        : st === "CANCELLED"
          ? "stake-report-status--cancelled"
          : "stake-report-status--default";
  return <span className={`stake-report-status ${cls}`}>{status ?? "—"}</span>;
};

export const PaidUnpaidBadge = ({ status }) => {
  const st = String(status ?? "").toLowerCase();
  const isPaid = st === "paid";
  return (
    <span
      className={`stake-report-paid-badge ${
        isPaid ? "stake-report-paid-badge--paid" : "stake-report-paid-badge--unpaid"
      }`}
    >
      {isPaid ? "Paid" : "Unpaid"}
    </span>
  );
};

const normalizePeriodStatus = (period) => {
  const raw = period?.status ?? period?.payment_status ?? period?.payout_status;
  if (raw != null && String(raw).trim() !== "") {
    const st = String(raw).toLowerCase();
    if (st === "paid") return "paid";
    if (st === "unpaid" || st === "pending") return "unpaid";
  }
  if (period?.paid_at || period?.credited_at || period?.paid === true) return "paid";
  return "unpaid";
};

export const getPayoutPeriodsSummary = (periods) => {
  const rows = Array.isArray(periods) ? periods : [];
  const paidCount = rows.filter((p) => normalizePeriodStatus(p) === "paid").length;
  return { total: rows.length, paid: paidCount, unpaid: rows.length - paidCount };
};

const getPeriodNumber = (period, index) =>
  period?.period ??
  period?.period_number ??
  period?.day ??
  period?.period_index ??
  index + 1;

const getPeriodDate = (period) =>
  period?.due_date ??
  period?.scheduled_at ??
  period?.payout_date ??
  period?.period_date ??
  period?.date ??
  null;

const getPeriodAmount = (period) =>
  period?.amount ??
  period?.expected_amount ??
  period?.payout_amount ??
  period?.commission_amount ??
  null;

const getPeriodPaidAt = (period) => period?.paid_at ?? period?.credited_at ?? null;

const formatPeriodStatusExportLabel = (status) =>
  status === "paid" ? "Paid" : "Unpaid";

/** One-line per period for main Excel sheet, e.g. "P1 | 04/08/2026 | 106 | Paid" */
export const formatPeriodsDetailForExport = (periods, { includeLevel = false } = {}) => {
  const rows = Array.isArray(periods) ? periods : [];
  if (rows.length === 0) return "";
  return rows
    .map((period, index) => {
      const num = getPeriodNumber(period, index);
      const date = formatDateExport(getPeriodDate(period));
      const amount = formatNumPlain(getPeriodAmount(period));
      const status = formatPeriodStatusExportLabel(normalizePeriodStatus(period));
      const level =
        includeLevel && period?.level != null
          ? `L${String(period.level).replace(/^level/i, "")} | `
          : "";
      return `P${num} | ${level}${date || "—"} | ${amount || "—"} | ${status}`;
    })
    .join("; ");
};

export const getPayoutPeriodsExportFields = (row) => {
  const roiSummary = getPayoutPeriodsSummary(row?.roi_payout_periods);
  const referralSummary = getPayoutPeriodsSummary(row?.referral_payout_periods);
  return {
    "ROI Periods Paid": roiSummary.total > 0 ? `${roiSummary.paid}/${roiSummary.total}` : "",
    "Referral Periods Paid":
      referralSummary.total > 0 ? `${referralSummary.paid}/${referralSummary.total}` : "",
    "ROI Periods Detail": formatPeriodsDetailForExport(row?.roi_payout_periods),
    "Referral Periods Detail": formatPeriodsDetailForExport(row?.referral_payout_periods, {
      includeLevel: true,
    }),
  };
};

export const getUserPlanExportIdentity = (row) => ({
  subscriptionId: row?._id ?? row?.id ?? "",
  uuid: row?.user?.uuid ?? "",
  email: row?.user?.email ?? "",
  name: userFullName(row?.user) === "—" ? "" : userFullName(row?.user),
});

export const getStakeReportExportIdentity = (row) => ({
  subscriptionId: row?.subscription_id ?? "",
  uuid: row?.staker?.uuid ?? "",
  email: row?.staker?.email ?? "",
  name: userFullName(row?.staker) === "—" ? "" : userFullName(row?.staker),
});

export const buildRoiPeriodsSheetRows = (rows, getIdentity = getStakeReportExportIdentity) => {
  const sheetRows = [];
  rows.forEach((row) => {
    const identity = getIdentity(row);
    const periods = Array.isArray(row?.roi_payout_periods) ? row.roi_payout_periods : [];
    periods.forEach((period, index) => {
      const status = normalizePeriodStatus(period);
      sheetRows.push({
        "Subscription ID": identity.subscriptionId,
        UUID: identity.uuid,
        Email: identity.email,
        Name: identity.name,
        Period: getPeriodNumber(period, index),
        "Due Date": formatDateExport(getPeriodDate(period)),
        Amount: formatNumPlain(getPeriodAmount(period)),
        Status: formatPeriodStatusExportLabel(status),
        "Paid At": status === "paid" ? formatDateExport(getPeriodPaidAt(period)) : "",
      });
    });
  });
  return sheetRows;
};

export const buildReferralPeriodsSheetRows = (
  rows,
  getIdentity = getStakeReportExportIdentity
) => {
  const sheetRows = [];
  rows.forEach((row) => {
    const identity = getIdentity(row);
    const periods = Array.isArray(row?.referral_payout_periods) ? row.referral_payout_periods : [];
    periods.forEach((period, index) => {
      const status = normalizePeriodStatus(period);
      sheetRows.push({
        "Subscription ID": identity.subscriptionId,
        UUID: identity.uuid,
        Email: identity.email,
        Name: identity.name,
        Period: getPeriodNumber(period, index),
        Level:
          period?.level != null ? String(period.level).replace(/^level/i, "L") : "",
        "Due Date": formatDateExport(getPeriodDate(period)),
        Amount: formatNumPlain(getPeriodAmount(period)),
        Status: formatPeriodStatusExportLabel(status),
        "Paid At": status === "paid" ? formatDateExport(getPeriodPaidAt(period)) : "",
      });
    });
  });
  return sheetRows;
};

export const PayoutPeriodsTable = ({ periods, showLevel = false, emptyText }) => {
  const rows = Array.isArray(periods) ? periods : [];
  if (rows.length === 0) {
    return <p className="openorders-empty">{emptyText}</p>;
  }

  const hasLevel = showLevel || rows.some((p) => p?.level != null);

  return (
    <div className="stake-report-table-wrap">
      <table className="stake-report-table">
        <thead>
          <tr>
            <th>Period</th>
            {hasLevel ? <th>Level</th> : null}
            <th>Due / scheduled</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Paid at</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((period, index) => {
            const status = normalizePeriodStatus(period);
            const paidAt = getPeriodPaidAt(period);
            const rowKey =
              period?.id ??
              `${getPeriodNumber(period, index)}-${getPeriodDate(period)}-${period?.level ?? ""}-${status}`;
            return (
              <tr key={rowKey}>
                <td>{getPeriodNumber(period, index)}</td>
                {hasLevel ? (
                  <td>
                    {period?.level != null
                      ? String(period.level).replace(/^level/i, "L")
                      : "—"}
                  </td>
                ) : null}
                <td>{formatDate(getPeriodDate(period))}</td>
                <td>{formatNum(getPeriodAmount(period))}</td>
                <td>
                  <PaidUnpaidBadge status={status} />
                </td>
                <td>{status === "paid" ? formatDate(paidAt) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const PayoutPeriodsSummary = ({ periods }) => {
  const rows = Array.isArray(periods) ? periods : [];
  if (rows.length === 0) return null;
  const paidCount = rows.filter((p) => normalizePeriodStatus(p) === "paid").length;
  const unpaidCount = rows.length - paidCount;
  return (
    <KvGrid
      items={[
        { label: "Total periods", value: rows.length },
        { label: "Paid", value: paidCount },
        { label: "Unpaid", value: unpaidCount },
      ]}
    />
  );
};

const DetailSection = ({ title, icon, children }) => (
  <section className="stake-report-detail-section">
    <h3 className="stake-report-detail-section__title">
      {icon ? <i className={`fas ${icon}`} aria-hidden /> : null}
      {title}
    </h3>
    <div className="stake-report-detail-section__body">{children}</div>
  </section>
);

const KvGrid = ({ items }) => (
  <dl className="stake-report-kv-grid">
    {items.map(({ label, value }) => (
      <div key={label} className="stake-report-kv">
        <dt>{label}</dt>
        <dd>{value ?? "—"}</dd>
      </div>
    ))}
  </dl>
);

const UserKvGrid = ({ user, prefix = "" }) => {
  if (!user) return <p className="openorders-empty">No user data.</p>;
  return (
    <KvGrid
      items={[
        { label: `${prefix}UUID`, value: user.uuid },
        { label: `${prefix}Email`, value: user.email ?? user.emailId },
        { label: `${prefix}Phone`, value: user.phone ?? user.mobileNumber },
        { label: `${prefix}Name`, value: userFullName(user) },
        { label: `${prefix}Payout status`, value: user.payout_status },
        { label: `${prefix}User ID`, value: user.id ?? user._id },
      ]}
    />
  );
};

const ReferralPayoutsSection = ({ referralPayouts }) => {
  const [activeLevel, setActiveLevel] = useState("level1");
  if (!referralPayouts) return null;

  const byLevel = referralPayouts.by_level || {};
  const byLevelTotals = referralPayouts.by_level_totals || {};
  const rows = Array.isArray(byLevel[activeLevel]) ? byLevel[activeLevel] : [];

  return (
    <DetailSection title="Referral payouts" icon="fa-sitemap">
      <KvGrid
        items={[
          { label: "Total distributed", value: formatNum(referralPayouts.total_distributed) },
          { label: "Transaction count", value: referralPayouts.count ?? "—" },
          ...REFERRAL_LEVELS.map((lv) => ({
            label: `${lv.replace("level", "Level ")} total`,
            value: formatNum(byLevelTotals[lv]),
          })),
        ]}
      />

      <div className="stake-report-level-tabs">
        {REFERRAL_LEVELS.map((lv) => (
          <button
            key={lv}
            type="button"
            className={`stake-report-level-tab ${activeLevel === lv ? "stake-report-level-tab--active" : ""}`}
            onClick={() => setActiveLevel(lv)}
          >
            {lv.replace("level", "L")}
            {byLevelTotals[lv] != null ? ` · ${formatNum(byLevelTotals[lv])}` : ""}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="openorders-empty">No referral payouts for this level.</p>
      ) : (
        <div className="stake-report-table-wrap">
          <table className="stake-report-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Amount</th>
                <th>%</th>
                <th>Base</th>
                <th>Recipient</th>
                <th>Credited at</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id ?? `${r.level}-${r.credited_at}-${r.commission_type}`}>
                  <td>{r.commission_type ?? "—"}</td>
                  <td>{formatNum(r.commission_amount)}</td>
                  <td>{r.commission_percent != null ? `${r.commission_percent}%` : "—"}</td>
                  <td>{formatNum(r.base_amount)}</td>
                  <td>
                    {userFullName(r.recipient)}
                    <br />
                    <small>{r.recipient?.email ?? r.recipient?.uuid ?? "—"}</small>
                  </td>
                  <td>{formatDate(r.credited_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DetailSection>
  );
};

export const StakeReportDetailContent = ({ row }) => {
  if (!row) return null;

  const staker = row.staker;
  const pkg = row.package;
  const staking = row.staking;
  const roi = row.roi_payouts;
  const summary = row.summary;
  const levelCommissions = pkg?.level_commissions || {};

  return (
    <div className="stake-report-detail-content">
      <div className="stake-report-detail-hero">
        <div>
          <p className="stake-report-detail-hero__eyebrow">Subscription</p>
          <h2 className="stake-report-detail-hero__title">{userFullName(staker)}</h2>
          <p className="stake-report-detail-hero__sub">
            {staker?.email ?? "—"}
            {staker?.uuid ? ` · ${staker.uuid}` : ""}
          </p>
        </div>
        <StatusBadge status={row.status} />
      </div>

      <div className="stake-report-detail-summary-cards">
        <div className="stake-report-detail-summary-card">
          <span className="stake-report-detail-summary-card__label">Invested</span>
          <span className="stake-report-detail-summary-card__value">
            {formatNum(summary?.invested_amount ?? staking?.invested_amount)}{" "}
            {staking?.currency ?? pkg?.currency ?? ""}
          </span>
        </div>
        <div className="stake-report-detail-summary-card">
          <span className="stake-report-detail-summary-card__label">ROI paid</span>
          <span className="stake-report-detail-summary-card__value">
            {formatNum(summary?.total_roi_paid_to_staker ?? staking?.total_roi_paid)}
          </span>
        </div>
        <div className="stake-report-detail-summary-card">
          <span className="stake-report-detail-summary-card__label">Referral paid</span>
          <span className="stake-report-detail-summary-card__value">
            {formatNum(summary?.total_referral_commission_paid ?? staking?.referral_commission_total_paid)}
          </span>
        </div>
        <div className="stake-report-detail-summary-card">
          <span className="stake-report-detail-summary-card__label">Net liability</span>
          <span className="stake-report-detail-summary-card__value">
            {formatNum(summary?.net_platform_liability)}
          </span>
        </div>
      </div>

      <DetailSection title="Subscription" icon="fa-file-alt">
        <KvGrid
          items={[
            { label: "Subscription ID", value: row.subscription_id },
            { label: "Status", value: <StatusBadge status={row.status} /> },
            { label: "Created at", value: formatDate(row.created_at) },
            { label: "Updated at", value: formatDate(row.updated_at) },
          ]}
        />
      </DetailSection>

      <DetailSection title="Staker" icon="fa-user">
        <UserKvGrid user={staker} />
      </DetailSection>

      <DetailSection title="Upline chain" icon="fa-users">
        {!Array.isArray(row.upline_chain) || row.upline_chain.length === 0 ? (
          <p className="openorders-empty">No upline sponsors.</p>
        ) : (
          <div className="stake-report-table-wrap">
            <table className="stake-report-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>UUID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Payout</th>
                </tr>
              </thead>
              <tbody>
                {row.upline_chain.map((entry) => (
                  <tr key={`${entry.level}-${entry.user?.id ?? entry.user?.uuid}`}>
                    <td>L{entry.level}</td>
                    <td>{entry.user?.uuid ?? "—"}</td>
                    <td>{userFullName(entry.user)}</td>
                    <td>{entry.user?.email ?? "—"}</td>
                    <td>{entry.user?.phone ?? "—"}</td>
                    <td>{entry.user?.payout_status ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailSection>

      <DetailSection title="Package" icon="fa-box">
        <KvGrid
          items={[
            { label: "Currency", value: pkg?.currency },
            { label: "Full name", value: pkg?.currency_fullname },
            { label: "Duration (days)", value: pkg?.duration_days },
            { label: "Return %", value: pkg?.return_percentage != null ? `${pkg.return_percentage}%` : "—" },
            { label: "Return type", value: pkg?.return_type },
            { label: "Payout status", value: pkg?.payout_status },
            { label: "Referral commission type", value: pkg?.referral_commission_type },
            { label: "L1 commission", value: levelCommissions.level1 != null ? `${levelCommissions.level1}%` : "—" },
            { label: "L2 commission", value: levelCommissions.level2 != null ? `${levelCommissions.level2}%` : "—" },
            { label: "L3 commission", value: levelCommissions.level3 != null ? `${levelCommissions.level3}%` : "—" },
            { label: "L4 commission", value: levelCommissions.level4 != null ? `${levelCommissions.level4}%` : "—" },
            { label: "L5 commission", value: levelCommissions.level5 != null ? `${levelCommissions.level5}%` : "—" },
            {
              label: "L1 instant bonus",
              value:
                levelCommissions.level1_instant_bonus != null
                  ? `${levelCommissions.level1_instant_bonus}%`
                  : "—",
            },
          ]}
        />
      </DetailSection>

      <DetailSection title="Staking" icon="fa-coins">
        <KvGrid
          items={[
            { label: "Invested", value: formatNum(staking?.invested_amount) },
            { label: "Expected return", value: formatNum(staking?.expected_return) },
            { label: "Total ROI paid", value: formatNum(staking?.total_roi_paid) },
            { label: "Remaining", value: formatNum(staking?.remaining_amount) },
            { label: "Paid days", value: staking?.paid_days ?? "—" },
            { label: "Duration (days)", value: staking?.duration_days ?? "—" },
            { label: "Subscription type", value: staking?.subscription_type },
            { label: "With principal", value: staking?.with_principal_amount ? "Yes" : "No" },
            { label: "Start date", value: formatDate(staking?.start_date) },
            { label: "End date", value: formatDate(staking?.end_date) },
            { label: "Next payout", value: formatDate(staking?.next_payout_date) },
            { label: "Last payout", value: formatDate(staking?.last_payout_at) },
            { label: "Wallet type", value: staking?.wallet_type },
            { label: "Credited wallet", value: staking?.credited_wallet_type },
            { label: "Purchased by", value: staking?.purchased_by },
            { label: "Instant bonus credited", value: staking?.instant_bonus_credited ? "Yes" : "No" },
            { label: "Referral commission type", value: staking?.referral_commission_type },
            { label: "Referral paid periods", value: staking?.referral_commission_paid_periods ?? "—" },
            { label: "Referral total paid", value: formatNum(staking?.referral_commission_total_paid) },
            { label: "Next referral commission", value: formatDate(staking?.next_referral_commission_date) },
            { label: "USDT value", value: formatNum(staking?.usdt_value) },
            { label: "Notes", value: staking?.notes || "—" },
          ]}
        />
      </DetailSection>

      <DetailSection title="ROI payouts" icon="fa-chart-line">
        <KvGrid
          items={[
            { label: "Count", value: roi?.count ?? "—" },
            { label: "Total paid", value: formatNum(roi?.total_paid) },
          ]}
        />
        {!Array.isArray(roi?.history) || roi.history.length === 0 ? (
          <p className="openorders-empty">No ROI payout history.</p>
        ) : (
          <div className="stake-report-table-wrap">
            <table className="stake-report-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Paid at</th>
                  <th>Next payout</th>
                </tr>
              </thead>
              <tbody>
                {roi.history.map((h) => (
                  <tr key={`${h.day}-${h.paid_at}`}>
                    <td>{h.day ?? "—"}</td>
                    <td>{formatNum(h.amount)}</td>
                    <td>{h.type ?? "—"}</td>
                    <td>{formatDate(h.paid_at)}</td>
                    <td>{formatDate(h.next_payout_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailSection>

      <DetailSection title="ROI payout periods" icon="fa-calendar-check">
        <PayoutPeriodsSummary periods={row.roi_payout_periods} />
        <PayoutPeriodsTable
          periods={row.roi_payout_periods}
          emptyText="No ROI payout periods."
        />
      </DetailSection>

      <ReferralPayoutsSection referralPayouts={row.referral_payouts} />

      <DetailSection title="Referral payout periods" icon="fa-calendar-alt">
        <PayoutPeriodsSummary periods={row.referral_payout_periods} />
        <PayoutPeriodsTable
          periods={row.referral_payout_periods}
          showLevel
          emptyText="No referral payout periods."
        />
      </DetailSection>

      {row.cancellation ? (
        <DetailSection title="Cancellation" icon="fa-ban">
          <KvGrid
            items={Object.entries(row.cancellation).map(([key, value]) => ({
              label: key.replace(/_/g, " "),
              value:
                typeof value === "string" && value.includes("T")
                  ? formatDate(value)
                  : String(value ?? "—"),
            }))}
          />
        </DetailSection>
      ) : null}

      <DetailSection title="Summary" icon="fa-calculator">
        <KvGrid
          items={[
            { label: "Invested amount", value: formatNum(summary?.invested_amount) },
            { label: "Total ROI paid to staker", value: formatNum(summary?.total_roi_paid_to_staker) },
            { label: "Pending ROI", value: formatNum(summary?.pending_roi) },
            { label: "Total referral commission paid", value: formatNum(summary?.total_referral_commission_paid) },
            { label: "Net platform liability", value: formatNum(summary?.net_platform_liability) },
          ]}
        />
      </DetailSection>
    </div>
  );
};

export const stakeReportRowToExport = (row) => {
  const staker = row.staker || {};
  const staking = row.staking || {};
  const summary = row.summary || {};
  const pkg = row.package || {};
  return {
    "Subscription ID": row.subscription_id ?? "",
    Status: row.status ?? "",
    "Staker UUID": staker.uuid ?? "",
    "Staker Name": userFullName(staker) === "—" ? "" : userFullName(staker),
    "Staker Email": staker.email ?? "",
    "Staker Phone": staker.phone ?? "",
    "Payout Status": staker.payout_status ?? "",
    Currency: pkg.currency ?? pkg.currency_short_name ?? staking.currency ?? "",
    "Package Name": pkg.currency_fullname ?? "",
    "Duration (days)": pkg.duration_days ?? staking.duration_days ?? "",
    Invested: formatNumPlain(staking.invested_amount ?? summary.invested_amount),
    "Expected Return": formatNumPlain(staking.expected_return),
    "Total ROI Paid": formatNumPlain(staking.total_roi_paid ?? summary.total_roi_paid_to_staker),
    "Referral Paid": formatNumPlain(
      staking.referral_commission_total_paid ?? summary.total_referral_commission_paid
    ),
    "Net Liability": formatNumPlain(summary.net_platform_liability),
    "USDT Value": formatNumPlain(staking.usdt_value),
    "Start Date": formatDateExport(staking.start_date),
    "End Date": formatDateExport(staking.end_date),
    "Created At": formatDateExport(row.created_at),
    ...getPayoutPeriodsExportFields(row),
  };
};
